import { LiteState } from '@map/components/lite/utils';
import { buildApiUrl } from '@map/hooks/baseApi';
import { Profile } from '@map/types';
import useAction from '@shared/hooks/useAction';
import { deepEqual } from '@shared/util/object';
import axios, { type AxiosRequestConfig } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, Reducer } from 'redux';
import { createSelector } from 'reselect';

const windowWithInvitation = window as Window & {
  invitation?: { first_name: string; last_name: string };
};

const defaultAttr: Partial<Profile> = {
  first_name: windowWithInvitation.invitation?.first_name || '',
  last_name: windowWithInvitation.invitation?.last_name || '',
  email: '',
  telephone: '',
  birthday: '',
  nationality: '',
  arrival_in_country: '',
  enable_data_share: false,
  street_and_house_number: '',
  city: '',
  postcode: '',
  country: '',
  gender: null,
  gender_custom: '',
  disabilities: [],
  low_chances: [],
  tours: {},
  avatar_urls: null,
  accessible_features: [],
};

// different from lodash.defaults in that we ignore null values. Using
// empty strings in place of nulls is necessary for form elements
const setDefaults = (attr: Partial<Profile>) => {
  const result = { ...attr };
  (Object.keys(defaultAttr) as Array<keyof typeof defaultAttr>).forEach((key) => {
    if (result[key] === undefined || result[key] === null) {
      (result as any)[key] = defaultAttr[key] as any;
    }
  });
  return result as Profile;
};

type State = Partial<Profile>;

export const PROFILE_FETCHED = 'PROFILE_FETCHED';
const PROFILE_UPDATED = 'PROFILE_UPDATED';

export const actionCreators = {
  profileFetched: (data: Profile) => ({ type: PROFILE_FETCHED, data }) as const,
  profileUpdated: (data: Partial<Profile>) => ({ type: PROFILE_UPDATED, data }) as const,
};

type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const profile: Reducer<State, Action> = (state = defaultAttr, action) => {
  switch (action.type) {
    case PROFILE_UPDATED:
      return setDefaults(action.data);
    case PROFILE_FETCHED: {
      const newState = setDefaults(action.data);
      // we store this in a different store slice
      delete newState.tips;
      // if we are signed in on the server or the client
      // then sync the local state to remote
      if ((newState.id || state.id) && !deepEqual(newState, state)) {
        return newState;
      }
      // we are in the onboarding process so don't
      // wipe local state that hasn't been persisted
      return state;
    }
    default:
      return state;
  }
};

export default profile;

// SELECTORS
const rootSelector = (state: { profile: Profile }) => state.profile;
export const useCurrentProfile = () => useSelector(rootSelector);
const mentorSelector = createSelector(rootSelector, (profile) => profile.is_mentor);
export const useIsMentor = () => useSelector(mentorSelector);

const cube8Selector = createSelector(rootSelector, (profile) => !!profile.cube8_code);
export const useIsCareerInterestsTestComplete = () => useSelector(cube8Selector);

const mentorViewSelector = createSelector(
  rootSelector,
  ({ is_mentor: isMentor, mentor_view: mentorView }) => isMentor && mentorView,
);

export const useIsMentorView = () => useSelector(mentorViewSelector);
const liteSelector = createSelector(rootSelector, (profile) => profile.is_lite || false);
export const useIsLiteProfile = () => useSelector(liteSelector);

const emptyLiteState: LiteState[] = [];
export const liteStatesSelector = createSelector(
  rootSelector,
  (profile): LiteState[] => (profile.lite_state as LiteState[] | undefined) ?? emptyLiteState,
);
export const useLiteStates = () => useSelector(liteStatesSelector);

const updateProfile =
  (
    profile: Partial<Omit<Profile, 'tours'>> & { access_code?: string; tours?: string[] },
    silent: AxiosRequestConfig['handlerDisabled'] = false,
  ) =>
  async (dispatch: Dispatch) => {
    const res = await axios.put(buildApiUrl('profiles'), { profile }, { handlerDisabled: silent });
    return dispatch(actionCreators.profileUpdated(res.data.profile));
  };

export const useUpdateProfile = () => useAction(updateProfile);

export const useMutateProfile = () => {
  const dispatch = useDispatch();
  const currentProfile = useCurrentProfile();
  return (data: Partial<Profile>) => {
    dispatch(actionCreators.profileUpdated({ ...currentProfile, ...data }));
  };
};

const uploadAvatar = (avatar: File) => async (dispatch: Dispatch) => {
  const data = new FormData();
  data.append('profile[avatar]', avatar);
  const res = await axios.put(buildApiUrl('profiles'), data, { handlerDisabled: true });
  return dispatch(actionCreators.profileUpdated(res.data.profile));
};

export const useUploadAvatar = () => useAction(uploadAvatar);

const deleteProfilePicture = () => async (dispatch: Dispatch) => {
  const res = await axios.put(buildApiUrl('profiles'), { profile: { avatar: null } });
  return dispatch(actionCreators.profileUpdated({ ...res.data.profile, avatar_urls: null }));
};

export const useDeleteProfilePicture = () => useAction(deleteProfilePicture);
